import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getGrantEntitlementLog} from 'src/openapi/admin/apis/EntitlementLogApi';
import {useAppDispatch} from 'src/store/hooks';

import {entitlementGrantChangeLogStateSelector} from '../slices/entitlement-grant-change-log-state.slice';
import {
    entitlementGrantChangeLogActions,
    entitlementGrantChangeLogDataSelector,
    entitlementGrantChangeLogResultSelector,
} from '../slices/entitlement-grant-change-log.slice';

import type {EntitlementGrantLogsCriteria} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementLogsPage} from 'src/openapi/admin/models/EntitlementLogsPage';


type GetEntitlementGrantLogCacheKeys = CacheKeys<undefined, undefined, EntitlementGrantLogsCriteria>;

type Props = {
    grantId: string,
};

export const useGetEntitlementGrantChangeLog = ({grantId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetEntitlementGrantLogCacheKeys) => apiDispatch(
        getGrantEntitlementLog({
            grantId,
            grantEntitlementLogsPageRequest: {
                pagination: {
                    pageSize: cacheKeys.pagination.pageSize,
                    currentPage: cacheKeys.pagination.currentPage,
                },
                actionName: cacheKeys.filters?.actionName as string | undefined,
                startDate: cacheKeys.filters?.startDate as string | undefined,
                endDate: cacheKeys.filters?.endDate as string | undefined,
                entitlementName: cacheKeys.filters?.entitlementName as string | undefined,
            },
        }),
    ), [grantId, apiDispatch]);

    const setLoadedData = useCallback((
        response: EntitlementLogsPage,
        cacheKeys: GetEntitlementGrantLogCacheKeys,
    ) => {
        dispatch(entitlementGrantChangeLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: entitlementGrantChangeLogDataSelector,
        stateSelector: entitlementGrantChangeLogStateSelector,
        resultSelector: entitlementGrantChangeLogResultSelector,
        actions: entitlementGrantChangeLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
